import React, { useState } from 'react';
import { Input, Button, ErrorSummary } from 'govuk-react-jsx';
import { useSelector, useDispatch } from 'react-redux';
import { Details } from 'govuk-react-jsx/govuk/components/details';
import { useHistory } from 'react-router';
import {
  getCurrentLanguage,
  getCopyProviderCY,
  getCopyProviderEN,
} from '../../features/app/appSlice';
import {
  getCompanyData,
  getBusinessVatData,
} from '../../features/registration/registrationJourneySlice';
import {
  getInstallerOzevNumber,
  setInstallerOzevNumber,
  setInstallerDetails,
  getGrantTypeSelected,
  getSelectedGrantValue,
} from '../../features/application/applicationJourneySlice';
import { getUserDetails } from '../../features/dashboard/dashboardSlice';
import { checkValidOzevNumber } from '../../regex/regex';
import { checkInstallerOZEVNumber } from '../../firebase';
import useTitle from '../../hooks/useTitle';

const InstallerDetails = () => {
  const currentLanguage = useSelector(getCurrentLanguage);
  const copyProviderCY = useSelector(getCopyProviderCY);
  const copyProviderEN = useSelector(getCopyProviderEN);
  const copyProvider =
    currentLanguage === 'en' ? copyProviderEN : copyProviderCY;
  useTitle(
    'landlordApp.InstallerDetails.s13',
    'Enter your Installer’s OZEV number - OZEV Electric Vehicle Chargepoint Grant - GOV.UK'
  );
  const companyDataRedux = useSelector(getCompanyData);
  const businessVatDataRedux = useSelector(getBusinessVatData);
  const ozevNumberRedux = useSelector(getInstallerOzevNumber);
  const grantDocumentId = useSelector(getGrantTypeSelected);
  const currentUserData = useSelector(getUserDetails);
  const selectedGrantObject = useSelector(getSelectedGrantValue);

  const dispatch = useDispatch();
  const history = useHistory();

  const [errorMsg, setErrorMsg] = useState('');
  const [errorList, setErrorList] = useState([]);

  const handleOzevNumber = (event) => {
    dispatch(setInstallerOzevNumber(event.target.value));
  };

  // Details component hyperlink
  const detailsHeading = copyProvider.getCopy(
    'landlordApp.InstallerDetails.s3',
    "I don't know my installer's number"
  );

  const formatErrorMsg = copyProvider.getCopy(
    'landlordApp.InstallerDetails.s5',
    'Enter a valid OZEV number which contains a minimum of 3 and a maximum of 5 numeric characters'
  );

  const notValidErrorMsg = copyProvider.getCopy(
    'landlordApp.InstallerDetails.s6',
    'Installer not authorised for this grant'
  );

  const notAuthorisedErrorMsg = copyProvider.getCopy(
    'landlordApp.InstallerDetails.s9',
    'OZEV installer number not found'
  );

  const handleBtnClick = () => {
    const showFormatError = {
      children: formatErrorMsg,
    };
    const showNotValidError = {
      children: notValidErrorMsg,
    };
    const showNotAuthorisedError = {
      children: notAuthorisedErrorMsg,
    };

    if (checkValidOzevNumber.test(ozevNumberRedux)) {
      setErrorMsg(null);
      checkInstallerOZEVNumber({
        installerOZEVNumber: ozevNumberRedux,
        grantDocumentId,
      })
        .then((response) => {
          const { data } = response;
          const isResidentialOrCarParks =
            selectedGrantObject?.grantTypeShort === 'residential properties' ||
            selectedGrantObject?.grantTypeShort === 'car parks';
          const isCommercialOrCommercialCarParks =
            selectedGrantObject?.grantTypeShort === 'commercial properties' ||
            selectedGrantObject?.grantTypeShort === 'commercial car parks';

          const isAuthorisedForResidential =
            data.installerDetails.authorised_for_residential;
          const isAuthorisedForCommercial =
            data.installerDetails.authorised_for_commercial;

          const isDataValid =
            data !== null &&
            data?.installerDetails?.installer_number === ozevNumberRedux &&
            data?.success === true;

          if (
            (isResidentialOrCarParks && !isAuthorisedForResidential) ||
            (isCommercialOrCommercialCarParks && !isAuthorisedForCommercial)
          ) {
            setErrorMsg(showNotAuthorisedError);
            setErrorList([
              { ...showNotAuthorisedError, href: '#InstallerDetails' },
            ]);
          } else if (
            isDataValid &&
            ((isResidentialOrCarParks && isAuthorisedForResidential) ||
              (isCommercialOrCommercialCarParks && isAuthorisedForCommercial))
          ) {
            dispatch(setInstallerDetails(data));
            history.push('/landlordApp/installer-confirm');
          } else {
            setErrorMsg(showNotValidError);
            setErrorList([{ ...showNotValidError, href: '#InstallerDetails' }]);
          }
        })
        .catch((error) => {
          if (error.code === 'functions/invalid-argument') {
            setErrorMsg(showNotValidError);
            setErrorList([{ ...showNotValidError, href: '#InstallerDetails' }]);
          } else if (error.code === 'functions/not-found') {
            setErrorMsg(showNotAuthorisedError);
            setErrorList([
              {
                ...showNotAuthorisedError,
                href: '#InstallerDetails',
              },
            ]);
          } else {
            setErrorMsg(showNotValidError);
            setErrorList([{ ...showNotValidError, href: '#InstallerDetails' }]);
          }
        });
    } else if (!checkValidOzevNumber.test(ozevNumberRedux)) {
      setErrorMsg(showFormatError);
      setErrorList([{ ...showFormatError, href: '#InstallerDetails' }]);
    }
  };

  const pageModel = (
    <>
      {errorList?.length > 0 && (
        <ErrorSummary
          errorList={errorList}
          titleChildren={copyProvider.getCopy(
            'Common.error-summery',
            'There is a problem'
          )}
        />
      )}
      <div className="govuk-form-group">
        <span className="govuk-caption-xl">
          {companyDataRedux && companyDataRedux.name}
          {businessVatDataRedux && businessVatDataRedux.target.name}
          {currentUserData != null &&
            currentUserData.business_name != null &&
            currentUserData.business_name}
        </span>
        <h1 className="govuk-heading-xl">
          {copyProvider.getCopy(
            'landlordApp.InstallerDetails.s1',
            'Enter your Installer’s OZEV number'
          )}
        </h1>
        <p className="govuk-body">
          {copyProvider.getCopy(
            'landlordApp.InstallerDetails.s2',
            'You need your installer’s OZEV number to continue your application for the grant.'
          )}
        </p>
        <div className="govuk-form-group">
          <div className="govuk-body">
            <Input
              errorMessage={errorMsg || null}
              label={{
                children: copyProvider.getCopy(
                  'landlordApp.InstallerDetails.s4',
                  'Installer’s OZEV number'
                ),
              }}
              id="InstallerDetails"
              className="govuk-input govuk-input--width-10"
              name="InstallerDetails"
              type="text"
              value={ozevNumberRedux}
              onChange={handleOzevNumber}
            />
          </div>

          <Details summaryChildren={detailsHeading}>
            <p>
              {copyProvider.getCopy(
                'landlordApp.InstallerDetails.s12',
                'Contact your installer and ask for this information. OZEV authorised installers receive this number when they register with OZEV.'
              )}
            </p>
          </Details>
        </div>
      </div>

      <Button className="govuk-button" onClick={handleBtnClick}>
        {copyProvider.getCopy('landlordApp.InstallerDetails.s7', 'Continue')}
      </Button>
    </>
  );

  return pageModel;
};

export default InstallerDetails;
